import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  ShieldCheckIcon, PlugsIcon, ChatCircleDotsIcon, PlayIcon,
  CircleNotchIcon, ArrowCounterClockwiseIcon, CheckCircleIcon, QuestionIcon,
  FilePlusIcon, PencilSimpleIcon, FileArrowUpIcon, XIcon, BriefcaseIcon,
  UserIcon, FolderOpenIcon, FileTextIcon, HashIcon, ClockIcon, GitDiffIcon,
} from "@phosphor-icons/react";

const CHANGE_PLACEHOLDER =
  "Describe the SCOPE of this change (this drives the rebuild) AND the relationship between the uploaded configs.\n\ne.g. Establish a new site-to-site IPsec VPN from 'branch2-fw.conf' to the hub in 'hub-fw.conf', advertise branch2 LAN 10.20.0.0/24 over BGP.";

const combineDocs = (docs) =>
  (docs || [])
    .map((d) => `===== ${String(d.kind || "doc").toUpperCase()}: ${d.filename || ""} =====\n${d.text || ""}`)
    .join("\n\n")
    .slice(0, 40000);

export const DesignConsole = ({ building, onBuild, onReset, activeJob, onJobConsumed, activeCO, onCOConsumed }) => {
  const [vendors, setVendors] = useState([]);
  const [selected, setSelected] = useState([]);
  const [versions, setVersions] = useState({});
  const [designMode, setDesignMode] = useState("new"); // new | revise

  // new (job-driven)
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [job, setJob] = useState(null);
  const [loadingJob, setLoadingJob] = useState(false);

  // revise
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [baseFiles, setBaseFiles] = useState([]);
  const [loadingProj, setLoadingProj] = useState(false);
  const [changeBrief, setChangeBrief] = useState("");
  const [changeHours, setChangeHours] = useState("");
  const [openCOs, setOpenCOs] = useState([]);
  const [coId, setCoId] = useState("");

  const [sow, setSow] = useState(""); // new: combined doc text; revise: change scope
  const [phase, setPhase] = useState("setup");
  const [loadingIv, setLoadingIv] = useState(false);
  const [summary, setSummary] = useState("");
  const [questions, setQuestions] = useState([]);
  const [ready, setReady] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [draft, setDraft] = useState({});
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/vendors").then(({ data }) => {
      setVendors(data);
      const v = {};
      data.forEach((d) => { v[d.id] = d.versions?.[0]; });
      setVersions(v);
    }).catch(() => {});
  }, []);

  const loadJobs = () => api.get("/jobs").then(({ data }) => setJobs(data)).catch(() => {});
  const loadProjects = () => api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  const loadCOs = () => api.get("/change-orders").then(({ data }) => setOpenCOs(data.filter((c) => c.status === "open" && c.project_known))).catch(() => {});
  useEffect(() => { loadJobs(); }, []);
  useEffect(() => { if (designMode === "revise") { loadProjects(); loadCOs(); } }, [designMode]);

  const selectCO = (id, coList, projList) => {
    setCoId(id);
    if (!id) return;
    const co = (coList || openCOs).find((c) => c.id === id);
    if (!co) return;
    setChangeBrief(co.title || "Change order");
    const scopeParts = [];
    if (co.scope) scopeParts.push(co.scope);
    if (co.reason && co.reason !== co.scope) scopeParts.push(`Reason: ${co.reason}`);
    if (co.bom_delta) {
      const b = co.bom_delta;
      const fmt = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");
      if (b.added?.length) scopeParts.push(`BoM added: ${fmt(b.added)}`);
      if (b.removed?.length) scopeParts.push(`BoM removed: ${fmt(b.removed)}`);
      if (b.modified?.length) scopeParts.push(`BoM modified: ${fmt(b.modified)}`);
    }
    setSow(scopeParts.join("\n"));
    const p = (projList || projects).find((x) => x.id === co.project_id);
    if (p) applyProject(p);
  };

  const selectJob = async (id) => {
    setJobId(id);
    setJob(null);
    setSow("");
    if (!id) return;
    setLoadingJob(true);
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
      setSow(combineDocs(data.documents));
      if (data.vendor_hint?.vendors?.length) {
        setSelected(data.vendor_hint.vendors);
        if (data.vendor_hint.versions) setVersions((s) => ({ ...s, ...data.vendor_hint.versions }));
      }
    } catch { toast.error("Failed to load job"); }
    setLoadingJob(false);
  };

  // preselect a job clicked from the Jobs panel
  useEffect(() => {
    if (activeJob?.id && phase === "setup") {
      setDesignMode("new");
      loadJobs();
      selectJob(activeJob.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeJob]);

  // preselect a signed change order clicked from the Jobs panel
  useEffect(() => {
    if (activeCO?.id && phase === "setup") {
      setDesignMode("revise");
      loadCOs();
      (async () => {
        try {
          const { data: projs } = await api.get("/projects");
          setProjects(projs);
          selectCO(activeCO.id, [activeCO], projs);
        } catch { /* noop */ }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCO]);

  const isRevise = designMode === "revise";
  const busy = building || loadingIv;
  const setupDisabled = phase !== "setup" || busy;

  const toggleVendor = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const pickVersions = () => {
    const out = {};
    selected.forEach((id) => { if (versions[id]) out[id] = versions[id]; });
    return out;
  };

  const applyProject = async (p) => {
    if (!p) return;
    setProjectId(p.id);
    setBaseFiles([]);
    if (Array.isArray(p.vendors) && p.vendors.length) setSelected(p.vendors);
    if (p.latest_gen_id) {
      setLoadingProj(true);
      try {
        const { data } = await api.get(`/history/${p.latest_gen_id}`);
        if (data?.versions && Object.keys(data.versions).length) setVersions((s) => ({ ...s, ...data.versions }));
        if (data?.config) setBaseFiles([{ filename: `${(p.name || "project").replace(/\s+/g, "-")}-v${p.latest_version || "0.1"}.conf`, content: data.config }]);
      } catch { /* manual upload still possible */ }
      setLoadingProj(false);
    }
  };

  const selectProject = (id) => {
    setCoId("");
    setProjectId(id);
    setBaseFiles([]);
    if (!id) return;
    applyProject(projects.find((x) => x.id === id));
  };

  const onFile = (e) => {
    Array.from(e.target.files || []).forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setBaseFiles((s) => [...s, { filename: f.name, content: String(reader.result || "") }]);
      reader.readAsText(f);
    });
    if (fileRef.current) fileRef.current.value = "";
  };
  const removeFile = (idx) => setBaseFiles((s) => s.filter((_, i) => i !== idx));

  const draftToAnswers = () =>
    questions.map((q) => ({ question: q.question, answer: (draft[q.id] || "").trim() })).filter((a) => a.answer);

  const payloadBase = (accAnswers) => ({
    vendors: selected, versions: pickVersions(), sow, answers: accAnswers,
    ...(isRevise
      ? { project_id: projectId, base_configs: baseFiles, change_brief: changeBrief,
          change_hours: changeHours === "" ? null : Number(changeHours),
          change_order_id: coId || undefined }
      : { source_job_id: jobId }),
  });

  const callInterview = async (accAnswers) => {
    const { data } = await api.post("/interview", payloadBase(accAnswers));
    setSummary(data.summary || "");
    setQuestions(data.questions || []);
    setReady(!!data.ready);
    setDraft({});
  };

  const validate = () => {
    if (isRevise) {
      if (!projectId) { toast.error("Select an existing project"); return false; }
      if (!baseFiles.length) { toast.error("Add at least one existing config file"); return false; }
      if (!changeBrief.trim()) { toast.error("Enter a brief for the change order"); return false; }
      if (!coId && (changeHours === "" || Number.isNaN(Number(changeHours)))) { toast.error("Enter the hours needed"); return false; }
    } else {
      if (!jobId || !job) { toast.error("Select an approved job to start from"); return false; }
    }
    if (!selected.length) { toast.error("Select at least one vendor"); return false; }
    if (!sow.trim()) { toast.error(isRevise ? "Describe the change scope first" : "The job has no document text to design from"); return false; }
    return true;
  };

  const startInterview = async () => {
    if (!validate()) return;
    setLoadingIv(true);
    try { setPhase("interview"); await callInterview([]); }
    catch (e) { toast.error(e?.response?.data?.detail || "Interview failed"); setPhase("setup"); }
    setLoadingIv(false);
  };

  const submitAnswers = async () => {
    const acc = [...answers, ...draftToAnswers()];
    setAnswers(acc);
    setLoadingIv(true);
    try { await callInterview(acc); }
    catch (e) { toast.error(e?.response?.data?.detail || "Interview failed"); }
    setLoadingIv(false);
  };

  const build = () => {
    if (!validate()) return;
    const acc = [...answers, ...draftToAnswers()];
    onBuild(payloadBase(acc));
    if (!isRevise) onJobConsumed?.();
    if (isRevise && coId) onCOConsumed?.();
  };

  const reset = () => {
    setPhase("setup"); setSummary(""); setQuestions([]); setReady(false);
    setAnswers([]); setDraft({});
    onReset?.();
  };
  const switchMode = (m) => { if (phase === "setup" && !busy) setDesignMode(m); };

  const inputCls = "w-full rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5 text-sm text-zinc-200 placeholder:text-zinc-700 focus:border-cyan-500/80 focus:outline-none focus:ring-1 focus:ring-cyan-500/40 disabled:opacity-70";
  const cardCls = "rounded-lg border border-zinc-800/70 bg-[#0c0d10] p-4";

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[#0a0b0d]" data-testid="design-console">
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {/* mode toggle */}
        <div className="flex rounded-md border border-zinc-800 bg-zinc-900 p-0.5 text-xs font-medium">
          <button data-testid="design-mode-new" onClick={() => switchMode("new")}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-sm px-3 py-1.5 transition-colors ${!isRevise ? "bg-cyan-500 text-zinc-950" : "text-zinc-400 hover:text-zinc-200"}`}>
            <FilePlusIcon weight="fill" className="h-3.5 w-3.5" /> New design
          </button>
          <button data-testid="design-mode-revise" onClick={() => switchMode("revise")}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-sm px-3 py-1.5 transition-colors ${isRevise ? "bg-cyan-500 text-zinc-950" : "text-zinc-400 hover:text-zinc-200"}`}>
            <PencilSimpleIcon weight="fill" className="h-3.5 w-3.5" /> Revise an existing project
          </button>
        </div>

        {/* STEP 1 */}
        {!isRevise ? (
          <section className={cardCls} data-testid="job-select-section">
            <StepLabel n="1" icon={<BriefcaseIcon className="h-3.5 w-3.5" />} text="Approved job" />
            <p className="mb-3 text-xs text-zinc-500">Start from an approved SOW job received from the proposal system. The PS code, approval name and documents come with it.</p>
            {jobs.filter((j) => j.status === "new").length === 0 && !job ? (
              <p className="rounded-md border border-dashed border-zinc-800 bg-zinc-900/40 px-3 py-4 text-center text-xs text-zinc-600">
                No new jobs yet. Open the <span className="text-cyan-400">Jobs</span> panel — approved SOWs from the proposal app appear here.
              </p>
            ) : (
              <select data-testid="job-select" value={jobId} disabled={setupDisabled}
                onChange={(e) => selectJob(e.target.value)} className={inputCls}>
                <option value="">— choose an approved job —</option>
                {jobs.filter((j) => j.status === "new" || j.id === jobId).map((j) => (
                  <option key={j.id} value={j.id}>{j.ps_code}{j.client ? ` · ${j.client}` : ""}</option>
                ))}
              </select>
            )}
            {loadingJob && <p className="mt-2 text-xs text-zinc-500">Loading job…</p>}
            {job && (
              <div className="mt-3 space-y-2 rounded-md border border-cyan-500/30 bg-cyan-500/[0.05] p-3" data-testid="job-detail">
                <div className="flex items-center gap-2 text-sm">
                  <HashIcon className="h-3.5 w-3.5 text-cyan-400" />
                  <span className="font-mono text-cyan-300" data-testid="job-ps-code">{job.ps_code}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-300"><UserIcon className="h-3.5 w-3.5 text-zinc-500" /> Approved by <span className="text-zinc-100" data-testid="job-approval">{job.approval_name || "—"}</span></div>
                <div className="flex items-center gap-2 text-xs text-zinc-300"><BriefcaseIcon className="h-3.5 w-3.5 text-zinc-500" /> Client <span className="text-zinc-100" data-testid="job-client">{job.client || "—"}</span></div>
                <div className="pt-1 space-y-1">
                  {(job.documents || []).map((d, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[11px] text-zinc-400" data-testid={`job-doc-${d.kind}`}>
                      <FileTextIcon className="h-3.5 w-3.5 text-zinc-600" />
                      <span className="uppercase text-zinc-500">{d.kind}</span>: {d.filename}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        ) : (
          <section className={cardCls} data-testid="project-picker-section">
            <StepLabel n="1" icon={<FolderOpenIcon className="h-3.5 w-3.5" />} text="Select project" />
            {openCOs.length > 0 && (
              <div className="mb-3" data-testid="co-picker">
                <p className="mb-1.5 flex items-center gap-1.5 text-[11px] text-cyan-300"><GitDiffIcon className="h-3.5 w-3.5" /> Signed change order from PSDoc (optional)</p>
                <select data-testid="co-select" value={coId} disabled={setupDisabled}
                  onChange={(e) => selectCO(e.target.value)} className={inputCls}>
                  <option value="">— none (manual change order) —</option>
                  {openCOs.map((c) => (
                    <option key={c.id} value={c.id}>{c.change_code}{c.title ? ` · ${c.title}` : ""}</option>
                  ))}
                </select>
                {coId && <p className="mt-1 text-[11px] text-zinc-500">Project, brief and scope pre-filled from the signed change order.</p>}
              </div>
            )}
            <p className="mb-3 text-xs text-zinc-500">Pick an existing project by PS code. Its latest config loads automatically as the base.</p>
            {projects.length === 0 ? (
              <p className="rounded-md border border-dashed border-zinc-800 bg-zinc-900/40 px-3 py-4 text-center text-xs text-zinc-600">No projects yet.</p>
            ) : (
              <select data-testid="project-select" value={projectId} disabled={setupDisabled}
                onChange={(e) => selectProject(e.target.value)} className={inputCls}>
                <option value="">— choose a project —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.project_code || p.name}{p.customer ? ` · ${p.customer}` : ""}</option>
                ))}
              </select>
            )}
          </section>
        )}

        {/* STEP 2 — vendor scope */}
        <section className={cardCls}>
          <StepLabel n="2" icon={<PlugsIcon className="h-3.5 w-3.5" />} text="Vendor scope" />
          <p className="mb-3 text-xs text-zinc-500">Select the platform(s) in this deployment.</p>
          <div className="space-y-2">
            {vendors.map((v) => {
              const on = selected.includes(v.id);
              return (
                <div key={v.id} className={`rounded-md border transition-colors ${on ? "border-cyan-500/60 bg-cyan-500/[0.06]" : "border-zinc-800 bg-zinc-900/40"}`}>
                  <button data-testid={`vendor-${v.id}`} disabled={setupDisabled} onClick={() => toggleVendor(v.id)}
                    className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left disabled:opacity-70">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-sm font-medium text-zinc-100">
                        <span className={`h-3.5 w-3.5 rounded-[3px] border ${on ? "border-cyan-400 bg-cyan-500" : "border-zinc-600"}`} />
                        {v.name}
                      </div>
                      <div className="mt-1 truncate text-[10px] uppercase tracking-wider text-zinc-600">{v.source_label}</div>
                    </div>
                  </button>
                  {on && (
                    <div className="flex items-center gap-2 border-t border-cyan-500/20 px-3 py-2">
                      <span className="text-[11px] text-zinc-500">Firmware</span>
                      <select data-testid={`version-${v.id}`} disabled={setupDisabled} value={versions[v.id] || ""}
                        onChange={(e) => setVersions((s) => ({ ...s, [v.id]: e.target.value }))}
                        className="rounded-sm border border-zinc-700 bg-zinc-950 px-2 py-1 text-xs text-zinc-200 focus:border-cyan-500 focus:outline-none">
                        {v.versions?.map((ver) => <option key={ver} value={ver}>{ver}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* revise: change order + existing configs */}
        {isRevise && (
          <>
            <section className={cardCls} data-testid="change-order-section">
              <StepLabel n="3" icon={<ClockIcon className="h-3.5 w-3.5" />} text="Change order" />
              <p className="mb-3 text-xs text-zinc-500">Logged for admin billing against this PS code (a change code is generated on build).</p>
              <div className="space-y-2.5">
                <input data-testid="change-brief-input" value={changeBrief} disabled={setupDisabled}
                  onChange={(e) => setChangeBrief(e.target.value)} placeholder="Brief — short title of this change" className={inputCls} />
                <div className="relative">
                  <ClockIcon className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-zinc-600" />
                  <input data-testid="change-hours-input" type="number" min="0" step="0.5" value={changeHours} disabled={setupDisabled}
                    onChange={(e) => setChangeHours(e.target.value)} placeholder="Hours needed (e.g. 4)" className={`${inputCls} pl-8`} />
                </div>
              </div>
            </section>

            <section className={cardCls} data-testid="revise-upload-section">
              <StepLabel n="4" icon={<FileArrowUpIcon className="h-3.5 w-3.5" />} text="Existing config files" />
              <p className="mb-3 text-xs text-zinc-500">Add one or more configs; reference them by filename in the scope below.</p>
              <div className="space-y-2">
                {baseFiles.map((f, i) => (
                  <div key={i} className="flex items-center justify-between rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2.5" data-testid={`base-config-loaded-${i}`}>
                    <div className="min-w-0">
                      <p className="truncate text-sm text-zinc-200" data-testid={`base-config-filename-${i}`}>{f.filename}</p>
                      <p className="text-[11px] text-zinc-500">{f.content.split("\n").length} lines loaded</p>
                    </div>
                    <button data-testid={`base-config-clear-${i}`} onClick={() => removeFile(i)} disabled={busy} className="text-zinc-500 hover:text-rose-400"><XIcon className="h-4 w-4" /></button>
                  </div>
                ))}
                <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-zinc-700 bg-zinc-900/40 py-5 text-center hover:border-cyan-500/50">
                  {loadingProj ? <CircleNotchIcon className="h-5 w-5 animate-spin text-zinc-500" /> : <FileArrowUpIcon className="h-5 w-5 text-zinc-500" />}
                  <span className="text-xs text-zinc-400">Add more <span className="text-cyan-400">.conf</span> / .txt files</span>
                  <input ref={fileRef} data-testid="base-config-upload" type="file" multiple accept=".conf,.txt,.cfg,text/plain" onChange={onFile} disabled={busy} className="hidden" />
                </label>
              </div>
            </section>
          </>
        )}

        {/* scope / change scope */}
        <section className={cardCls}>
          <StepLabel n={isRevise ? "5" : "3"} icon={<ShieldCheckIcon className="h-3.5 w-3.5" />}
            text={isRevise ? "Change scope" : "Scope of Work (from documents)"} />
          {isRevise ? (
            <textarea data-testid="sow-input" value={sow} disabled={setupDisabled}
              onChange={(e) => setSow(e.target.value)} placeholder={CHANGE_PLACEHOLDER}
              className="h-48 w-full resize-none rounded-md border border-zinc-800 bg-zinc-950 p-3 font-mono text-[13px] leading-relaxed text-zinc-200 placeholder:text-zinc-700 focus:border-cyan-500 focus:outline-none disabled:opacity-70" />
          ) : (
            <>
              <p className="mb-2 text-xs text-zinc-500">Extracted from the approved documents (editable if you need to trim/clarify).</p>
              <textarea data-testid="sow-input" value={sow} disabled={setupDisabled}
                onChange={(e) => setSow(e.target.value)} placeholder="Select a job above to load its SOW / Solution Overview / Proposal text here…"
                className="h-48 w-full resize-none rounded-md border border-zinc-800 bg-zinc-950 p-3 font-mono text-[13px] leading-relaxed text-zinc-200 placeholder:text-zinc-700 focus:border-cyan-500 focus:outline-none disabled:opacity-70" />
            </>
          )}
        </section>

        {/* interview */}
        {phase === "interview" && (
          <section className={cardCls} data-testid="interview-section">
            <StepLabel n={isRevise ? "6" : "4"} icon={<ChatCircleDotsIcon className="h-3.5 w-3.5" />} text="Design interview" />
            {summary && <div className="mb-3 rounded-md border border-zinc-800 bg-zinc-900/60 p-3 text-xs text-zinc-400"><span className="text-zinc-500">Understood: </span>{summary}</div>}
            {ready && questions.length === 0 ? (
              <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] p-3 text-sm text-emerald-300" data-testid="interview-ready">
                <CheckCircleIcon weight="fill" className="h-4 w-4" /> Enough detail gathered — ready to build.
              </div>
            ) : (
              <div className="space-y-4">
                {questions.map((q) => (
                  <div key={q.id} data-testid={`question-${q.id}`}>
                    <label className="flex items-start gap-1.5 text-sm text-zinc-200"><QuestionIcon className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" /><span>{q.question}</span></label>
                    {q.hint && <p className="ml-5 mt-1 text-[11px] italic text-zinc-600">{q.hint}</p>}
                    <input data-testid={`answer-${q.id}`} value={draft[q.id] || ""} disabled={busy}
                      onChange={(e) => setDraft((s) => ({ ...s, [q.id]: e.target.value }))} placeholder="Your answer (leave blank to skip)"
                      className="ml-5 mt-1.5 w-[calc(100%-1.25rem)] rounded-sm border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-sm text-zinc-200 placeholder:text-zinc-700 focus:border-cyan-500 focus:outline-none" />
                  </div>
                ))}
                {questions.length > 0 && (
                  <button data-testid="submit-answers-btn" onClick={submitAnswers} disabled={busy}
                    className="flex items-center gap-1.5 rounded-sm border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-60">
                    {loadingIv ? <CircleNotchIcon className="h-3.5 w-3.5 animate-spin" /> : <ChatCircleDotsIcon className="h-3.5 w-3.5" />} Submit answers & refine
                  </button>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      {/* action bar */}
      <div className="shrink-0 border-t border-zinc-800 bg-zinc-950 p-4">
        <div className="mb-2 flex items-center justify-between text-[11px] text-zinc-500">
          <span className="uppercase tracking-[0.2em]">Engine</span>
          <span className="inline-flex items-center gap-1.5 text-cyan-400"><span className="h-1.5 w-1.5 rounded-full bg-cyan-500" /> Grounded AI · Claude Sonnet 4.6</span>
        </div>
        {phase === "setup" ? (
          <div className="space-y-2">
            <button data-testid="start-interview-btn" onClick={startInterview} disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-sm bg-cyan-500 py-2.5 text-sm font-semibold text-zinc-950 transition-colors hover:bg-cyan-400 disabled:opacity-60">
              {loadingIv ? <><CircleNotchIcon className="h-4 w-4 animate-spin" /> Reviewing…</> : <><ChatCircleDotsIcon weight="fill" className="h-4 w-4" /> Start Design Interview</>}
            </button>
            <button data-testid="skip-build-btn" onClick={build} disabled={busy}
              className="w-full text-center text-[11px] text-zinc-500 hover:text-zinc-300 disabled:opacity-50">
              or skip questions and build now (best-effort)
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button data-testid="reset-btn" onClick={reset} disabled={building}
              className="flex items-center justify-center gap-1.5 rounded-sm border border-zinc-700 px-3 py-2.5 text-sm text-zinc-300 hover:bg-zinc-800 disabled:opacity-60"><ArrowCounterClockwiseIcon className="h-4 w-4" /></button>
            <button data-testid="build-config-btn" onClick={build} disabled={busy}
              className="flex flex-1 items-center justify-center gap-2 rounded-sm bg-cyan-500 py-2.5 text-sm font-semibold text-zinc-950 transition-colors hover:bg-cyan-400 disabled:opacity-60">
              {building ? <><CircleNotchIcon className="h-4 w-4 animate-spin" /> Building…</> : <><PlayIcon weight="fill" className="h-4 w-4" /> {isRevise ? "Create change order & rebuild" : "Build Configuration"}</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const StepLabel = ({ n, icon, text }) => (
  <div className="mb-3 flex items-center gap-2.5">
    <span className="flex h-5 w-5 items-center justify-center rounded bg-zinc-800/80 text-[10px] font-bold text-cyan-400 border border-zinc-700/50">{n}</span>
    <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-zinc-300">{icon}{text}</span>
  </div>
);
